import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { JButton } from "@/components/JButton";
import { toast } from "sonner";
import { z } from "zod";

export const Route = createFileRoute("/reset-password")({
  head: () => ({
    meta: [
      { title: "Reset password | Jozori جذوري" },
      { name: "description", content: "Choose a new password for your Jozori account." },
    ],
  }),
  component: ResetPasswordPage,
});

const pwSchema = z.string().min(8, "Password must be at least 8 characters").max(72);

function ResetPasswordPage() {
  const navigate = useNavigate();
  const [ready, setReady] = useState(false);
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);

  // Supabase recovery link sets the session via URL hash. Wait for it.
  useEffect(() => {
    const { data: sub } = supabase.auth.onAuthStateChange((event) => {
      if (event === "PASSWORD_RECOVERY" || event === "SIGNED_IN") setReady(true);
    });
    supabase.auth.getSession().then(({ data }) => {
      if (data.session) setReady(true);
    });
    return () => sub.subscription.unsubscribe();
  }, []);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    const r = pwSchema.safeParse(password);
    if (!r.success) return toast.error(r.error.issues[0].message);
    if (password !== confirm) return toast.error("Passwords do not match");
    setBusy(true);
    const { error } = await supabase.auth.updateUser({ password });
    setBusy(false);
    if (error) return toast.error(error.message);
    toast.success("Password updated. You're signed in.");
    navigate({ to: "/app" });
  };

  return (
    <div className="min-h-screen bg-background flex flex-col">
      <header className="px-5 py-5 flex items-center justify-between">
        <Link to="/" className="flex items-baseline gap-2">
          <span className="font-display-ar text-2xl font-black text-primary">جذوري</span>
          <span className="font-display-en text-lg font-extrabold text-foreground/40">/</span>
          <span className="font-display-en text-lg font-extrabold tracking-tight">JOZORI</span>
        </Link>
      </header>

      <main className="flex-1 flex items-center justify-center px-5 pb-10">
        <div className="w-full max-w-md bg-card border-2 border-clay/30 rounded-3xl shadow-2xl p-7 md:p-9">
          <h1 className="font-display-en text-3xl font-bold mb-1 tracking-tight">
            Choose a new password
          </h1>
          <p className="text-sm text-foreground/60 mb-2">
            Pick something at least 8 characters.
          </p>
          <p className="text-sm text-foreground/60 mb-6" dir="rtl">
            اختر كلمة مرور جديدة (٨ أحرف على الأقل).
          </p>

          {!ready ? (
            <div className="text-sm text-foreground/60 p-4 rounded-2xl bg-clay/10 border border-clay/30">
              Validating your reset link… If this takes more than a few
              seconds, request a new link from the{" "}
              <Link to="/forgot-password" className="text-primary font-bold">forgot password</Link> page.
            </div>
          ) : (
            <form onSubmit={submit} className="space-y-3">
              <input
                type="password"
                placeholder="New password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                minLength={8}
                maxLength={72}
                className="w-full px-4 py-3 rounded-2xl border-2 border-clay/40 bg-background focus:border-primary focus:outline-none"
              />
              <input
                type="password"
                placeholder="Confirm new password"
                value={confirm}
                onChange={(e) => setConfirm(e.target.value)}
                required
                minLength={8}
                maxLength={72}
                className="w-full px-4 py-3 rounded-2xl border-2 border-clay/40 bg-background focus:border-primary focus:outline-none"
              />
              <JButton type="submit" disabled={busy} className="w-full" size="lg">
                {busy ? "Updating…" : "Update password"}
              </JButton>
            </form>
          )}
        </div>
      </main>
    </div>
  );
}
